import { useState, FormEvent } from 'react';
import { Eye, EyeOff, Lock, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable';
import SEO from '@/components/SEO';
import logoCygnature from '@/assets/logo-cygnature.png';
import { toast } from 'sonner';
import { translateAuthError } from './translateAuthError';

type Mode = 'login' | 'forgot';

export default function AuthPage() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast.error(translateAuthError(error.message));
    } else {
      toast.success('Connexion réussie');
    }
  };

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/definir-mot-de-passe`,
    });
    setLoading(false);
    if (error) toast.error(translateAuthError(error.message));
    else {
      toast.success('Si un compte existe pour cet email, un lien de réinitialisation a été envoyé.');
      setMode('login');
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth('google', {
        redirect_uri: `${window.location.origin}/crm-pro-evenementiel`,
      });
      if (result.error) toast.error('Connexion Google échouée');
    } catch {
      toast.error('Connexion Google indisponible');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="CRM Pro — Connexion" description="Espace privé." noindex />
      <div className="min-h-screen flex items-center justify-center px-4 bg-slate-950">
        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-lg space-y-6">
          <div className="flex flex-col items-center text-center space-y-3">
            <img src={logoCygnature} alt="Cygnature" className="h-14 w-auto" />
            <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Lock className="text-amber-400" strokeWidth={1.5} size={18} />
            </div>
            <h1 className="text-xl font-serif text-slate-100">CRM Pro — Cygnature</h1>
            <p className="text-xs text-slate-400">
              {mode === 'login' ? 'Accès réservé. Les comptes sont créés sur invitation de l\'administrateur.' : 'Réinitialisation du mot de passe'}
            </p>
          </div>

          {mode === 'login' && (
            <>
              <form onSubmit={handleLogin} className="space-y-4" autoComplete="on" method="post">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                  <Input
                    id="login-email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-slate-950 border-slate-800 text-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-slate-300">Mot de passe</Label>
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      className="text-[11px] text-amber-400 hover:text-amber-300"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="login-password"
                      name="password"
                      type={showPwd ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10 bg-slate-950 border-slate-800 text-slate-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-200"
                      aria-label={showPwd ? 'Masquer' : 'Afficher'}
                    >
                      {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950">
                  {loading ? 'Connexion…' : 'Se connecter'}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-800" /></div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-widest"><span className="bg-slate-900 px-3 text-slate-500">ou</span></div>
              </div>

              <Button onClick={handleGoogle} disabled={loading} variant="outline" className="w-full bg-slate-950 border-slate-800 text-slate-100 hover:bg-slate-800">
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continuer avec Google
              </Button>
            </>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-4" autoComplete="on" method="post">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-slate-300">Email du compte</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-950 border-slate-800 text-slate-100"
                />
                <p className="text-[10px] text-slate-500">Un lien de réinitialisation sera envoyé si le compte existe.</p>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950">
                {loading ? 'Envoi…' : 'Envoyer le lien'}
              </Button>
              <button
                type="button"
                onClick={() => setMode('login')}
                className="w-full flex items-center justify-center gap-2 text-xs text-slate-400 hover:text-slate-200"
              >
                <ArrowLeft size={12} /> Retour à la connexion
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
